/**
 * CoverageSelector Component
 * Dropdown for selecting defensive coverage schemes
 * Requirements: 4.1, 4.2
 *
 * Keyboard behaviour lives in useListbox — see that file for why these three dropdowns are custom
 * rather than native <select>, and for the ARIA pattern they follow.
 */

import { useEffect, useState } from 'react';
import { CoverageType, CoverageSelectorProps } from '../types';
import { getAvailableCoverages, getCoverageDefinition } from '../rules';
import { usePremiumStatus, useListbox } from '../hooks';
// The gate list lives with the pricing copy so the paywall and the pricing page cannot disagree.
import { PREMIUM_COVERAGES } from '../utils';
import PremiumNotice from './PremiumNotice';
import './CoverageSelector.css';

// Coverages not yet implemented for trips formation
const TRIPS_COMING_SOON: string[] = [];

function CoverageSelector({
  selectedCoverage,
  onCoverageChange,
  disabled = false,
  formation = 'doubles',
  customDefenseState,
  completeDefenses = [],
  selectedCustomDefenseId,
  onSelectCustomDefense,
  id = 'coverage-select',
  labelledBy,
}: CoverageSelectorProps) {
  const availableCoverages = getAvailableCoverages(formation);
  const { isPremium, isLoading } = usePremiumStatus();

  /**
   * Which locked coverage the user just tried to pick, if any. Drives the inline upsell.
   */
  const [blockedCoverage, setBlockedCoverage] = useState<string | null>(null);

  const isLockedCoverage = (coverage: string) =>
    PREMIUM_COVERAGES.includes(coverage) && !isPremium && !isLoading;

  /**
   * The user's saved defenses are only listed once loaded, and they share one index space with the
   * built-in coverages so the keyboard runs over both as a single sequence.
   *
   * Built-ins occupy 0..availableCoverages.length-1; saved defenses follow. The visible group
   * heading is rendered between the two runs, which is why they are two loops below rather than one.
   */
  const customItems = customDefenseState?.status === 'loaded' ? completeDefenses : [];
  const customOffset = availableCoverages.length;
  const itemCount = customOffset + customItems.length;

  const selectedIndex = selectedCustomDefenseId
    ? customOffset + customItems.findIndex((defense) => defense.id === selectedCustomDefenseId)
    : availableCoverages.indexOf(selectedCoverage);

  const handleActivate = (index: number) => {
    if (index >= customOffset) {
      const defense = customItems[index - customOffset];
      if (defense) {
        onSelectCustomDefense?.(defense);
        listbox.close();
      }
      return;
    }

    const coverage = availableCoverages[index];
    if (!coverage) return;
    if (formation === 'trips' && TRIPS_COMING_SOON.includes(coverage)) return;

    // A locked row now explains itself instead of silently doing nothing. The dropdown stays open
    // so the notice is visible.
    if (isLockedCoverage(coverage)) {
      setBlockedCoverage(coverage);
      return;
    }

    setBlockedCoverage(null);
    onCoverageChange(coverage as CoverageType);
    listbox.close();
  };

  const listbox = useListbox({
    itemCount,
    selectedIndex,
    onActivate: handleActivate,
    id,
    disabled,
  });

  // A dismissed dropdown should not remember which lock was hit.
  useEffect(() => {
    if (!listbox.isOpen) setBlockedCoverage(null);
  }, [listbox.isOpen]);

  const selectedDefinition = getCoverageDefinition(selectedCoverage, formation);
  const activeCustomDefenseName = selectedCustomDefenseId
    ? completeDefenses.find((defense) => defense.id === selectedCustomDefenseId)?.name
    : undefined;
  const triggerLabel = selectedCustomDefenseId
    ? activeCustomDefenseName
    : selectedDefinition?.name ?? selectedCoverage;

  const lockedCount = availableCoverages.filter(isLockedCoverage).length;

  return (
    <div className="coverage-selector" ref={listbox.containerRef}>
      <div
        className={`coverage-select-trigger ${disabled ? 'disabled' : ''}`}
        id={id}
        // A <label htmlFor> cannot name a <div>, so a visible label is wired up with
        // aria-labelledby instead. SimulationPage used htmlFor against ids that did not exist.
        {...(labelledBy
          ? { 'aria-labelledby': labelledBy }
          : { 'aria-label': 'Select defensive coverage' })}
        {...listbox.triggerProps}
      >
        <span>{triggerLabel}</span>
        <span className="coverage-select-arrow" aria-hidden="true">▼</span>
      </div>

      {listbox.isOpen && !disabled && (
        <ul className="coverage-select-dropdown" {...listbox.listProps}>
          {availableCoverages.map((coverage, index) => {
            const definition = getCoverageDefinition(coverage, formation);
            const isLocked = isLockedCoverage(coverage);
            const isComingSoon = formation === 'trips' && TRIPS_COMING_SOON.includes(coverage);
            const isSelected = coverage === selectedCoverage && !selectedCustomDefenseId;
            const name = definition?.name ?? coverage;

            return (
              <li
                key={coverage}
                className={`coverage-option ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''} ${isComingSoon ? 'coming-soon' : ''}`}
                // Stated explicitly as well as via the spread below so static a11y linting can see
                // that aria-selected is valid here.
                role="option"
                aria-selected={isSelected}
                // Locked rows are deliberately NOT aria-disabled. They are reachable and they now
                // do something — explain the paywall — so calling them disabled would be a lie.
                {...listbox.getOptionProps(index)}
              >
                <span className="coverage-option-name">{name}</span>
                {isComingSoon && <span className="coverage-coming-soon-badge">Coming Soon</span>}
                {/* A word, not a padlock emoji: it says what the row costs, and it can inherit
                    brand colour, which an emoji cannot. */}
                {isLocked && !isComingSoon && <span className="coverage-premium-badge">Premium</span>}

                {blockedCoverage === coverage && (
                  <PremiumNotice
                    featureName={name}
                    detail={
                      lockedCount > 1
                        ? `Premium unlocks it and ${lockedCount - 1} more, plus the Trips formation and saved plays.`
                        : 'Premium unlocks it, plus the Trips formation and saved plays.'
                    }
                    onNavigate={listbox.close}
                  />
                )}
              </li>
            );
          })}

          {customDefenseState && (
            <>
              {/* aria-hidden because the heading is decoration for a sighted user; a screen reader
                  hears the defenses as the tail of the same list, which is accurate. */}
              <li className="coverage-group-label" role="presentation" aria-hidden="true">
                My Custom Defenses
              </li>
              {customDefenseState.status === 'loading' && (
                <li className="coverage-group-status" role="presentation">Loading…</li>
              )}
              {customDefenseState.status === 'error' && (
                <li className="coverage-group-status coverage-group-error" role="presentation">
                  Couldn't load your defenses
                </li>
              )}
              {customDefenseState.status === 'loaded' && customItems.length === 0 && (
                <li className="coverage-group-status" role="presentation">No saved defenses yet</li>
              )}
              {customItems.map((defense, offset) => {
                const index = customOffset + offset;
                const isSelected = defense.id === selectedCustomDefenseId;
                return (
                  <li
                    key={defense.id}
                    className={`coverage-option ${isSelected ? 'selected' : ''}`}
                    role="option"
                    aria-selected={isSelected}
                    {...listbox.getOptionProps(index)}
                  >
                    <span className="coverage-option-name">{defense.name}</span>
                  </li>
                );
              })}
            </>
          )}
        </ul>
      )}
    </div>
  );
}

export default CoverageSelector;
